
import React, { useState, useEffect } from "react";
import { CalendarSection } from "@/components/admin/calendar/CalendarSection";
import { BookingList } from "@/components/booking/BookingList";
import { ServiceReviewSection } from "@/components/service-details/ServiceReviewSection";
import EditServiceTab from "@/components/admin/calendar/EditServiceTab";
import CalendarSettingsTab from "@/components/admin/calendar/CalendarSettingsTab";
import { NotificationsSection } from "@/components/service-details/NotificationsSection";
import { useServiceEditAccess } from "@/hooks/useServiceEditAccess";
import { useIsMobile } from "@/hooks/use-mobile";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useLocation } from "react-router-dom";
import { parseStoredDateTime } from "@/utils/timeUtils";

interface DashboardContentProps {
  activeView: string;
  serviceId: string;
  selectedDate: Date;
  bookings: any[];
  serviceName: string;
  service?: any;
  onDateSelect: (date: Date) => void;
  onModifyBlock: (booking: any) => void;
  onDeleteBlock: (booking: any) => void;
  onViewBooking: (booking: any) => void;
  onAcceptBooking: (bookingId: number) => Promise<void>;
  onDeclineBooking: (bookingId: number) => Promise<void>;
  calendarView: string;
  onCalendarViewChange: (view: string) => void;
  onBookingSelect?: (booking: any) => void;
  onTimeSlotSelect?: (timeSlot: string) => void;
}

export const DashboardContent = ({
  activeView,
  serviceId,
  selectedDate,
  bookings,
  serviceName,
  service,
  onDateSelect,
  onModifyBlock,
  onDeleteBlock,
  onViewBooking,
  onAcceptBooking,
  onDeclineBooking,
  calendarView,
  onCalendarViewChange,
  onBookingSelect,
  onTimeSlotSelect
}: DashboardContentProps) => {
  const { canEditBasicInfo, canAccessCalendarSettings, isAdmin, isOwner } = useServiceEditAccess(serviceId);
  const isMobile = useIsMobile();
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const location = useLocation();
  
  const containerClass = isMobile 
    ? "bg-white rounded-lg shadow p-2 overflow-x-hidden"
    : "bg-white rounded-lg shadow p-4";

  // Check for URL query param and sessionStorage filter when component mounts or location changes
  useEffect(() => {
    if (activeView === "bookings") {
      // First check URL query params
      const searchParams = new URLSearchParams(location.search);
      const urlStatus = searchParams.get('status');
      
      if (urlStatus) {
        console.log("Found URL status filter:", urlStatus);
        setStatusFilter(urlStatus);
        return;
      }
      
      // If no URL param, check sessionStorage
      const savedFilter = sessionStorage.getItem('booking-status-filter');
      if (savedFilter) {
        console.log("Found saved filter:", savedFilter);
        setStatusFilter(savedFilter);
        // Clear it after using
        sessionStorage.removeItem('booking-status-filter');
      }
    }
  }, [activeView, location.search]);

  const filteredBookings = statusFilter 
    ? bookings.filter(b => {
        if (statusFilter === 'cancelled') {
          return b.status === 'cancelled' || b.status === 'declined';
        }
        return b.status === statusFilter;
      })
    : bookings;
  
  return (
    <div className="flex-1">
      {activeView === "calendar" && (
        <div className={containerClass}>
          <CalendarSection
            selectedDate={selectedDate}
            onDateSelect={onDateSelect}
            bookings={filteredBookings}
            serviceName={serviceName}
            onViewChange={onCalendarViewChange}
            service={service}
            onBookingSelect={onBookingSelect}
            onTimeSlotSelect={onTimeSlotSelect}
          />
        </div>
      )}

      {activeView === "bookings" && (
        <div className={containerClass}>
          <h2 className="text-lg font-semibold mb-3">Bookings</h2>
          
          {statusFilter && (
            <div className="flex mb-4 items-center space-x-2">
              <Badge variant={
                statusFilter === 'pending' ? 'default' :
                statusFilter === 'accepted' ? 'success' :
                statusFilter === 'cancelled' ? 'error' : 'default'
              }>
                {statusFilter.charAt(0).toUpperCase() + statusFilter.slice(1)}
              </Badge>
              <button 
                onClick={() => setStatusFilter(null)} 
                className="text-xs text-gray-500 hover:text-gray-700"
              >
                Clear filter
              </button>
            </div>
          )}
          
          <BookingList
            date={selectedDate}
            bookings={filteredBookings}
            onModifyBlock={onModifyBlock}
            onDeleteBlock={onDeleteBlock}
            onViewBooking={onViewBooking}
            onAcceptBooking={onAcceptBooking}
            onDeclineBooking={onDeclineBooking}
          />
        </div>
      )}

      {activeView === "reviews" && (
        <div className={containerClass}>
          <ServiceReviewSection serviceId={Number(serviceId)} />
        </div>
      )}
      
    </div>
  );
};
